import React, { useEffect, useMemo, useState } from "react";
import { Button, Drawer, Empty, Spin, Tag, Tooltip, Typography } from "antd";
import { HistoryOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";

import * as emailTemplateApi from "../../data/redux/services/emailTemplateApi";
import { notify } from "../../../services/toast.config";

import type { EmailTemplateVersion } from "../../data/redux/services/emailTemplateApi";
import type { EmailTemplate } from "../../data/redux/email-templates/types";

const { Text } = Typography;

interface Props {
  open: boolean;
  templateId: number | null;
  companyId?: string;
  companyType?: string;
  currentTemplate?: EmailTemplate | null;
  onClose: () => void;
  onRestored?: () => void;
}

const CURRENT_VERSION_SENTINEL_ID = -1;

const TemplateVersionsDrawer: React.FC<Props> = ({
  open,
  templateId,
  companyId,
  companyType,
  currentTemplate,
  onClose,
  onRestored,
}) => {
  const [versions, setVersions] = useState<EmailTemplateVersion[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState<number | null>(null);
  const [previewVersion, setPreviewVersion] =
    useState<EmailTemplateVersion | null>(null);

  const displayedVersions = useMemo<EmailTemplateVersion[]>(() => {
    if (!currentTemplate) return versions;
    const currentEntry: EmailTemplateVersion = {
      id: CURRENT_VERSION_SENTINEL_ID,
      template_id: currentTemplate.id,
      version: currentTemplate.version || 1,
      template_name: currentTemplate.template_name,
      template_code: currentTemplate.template_code,
      subject: currentTemplate.subject,
      body_html: currentTemplate.body_html,
      available_variables: (currentTemplate.available_variables as any) || null,
      category: currentTemplate.category || null,
      change_note: null,
      created_at: currentTemplate.updated_at,
    };
    const past = versions.filter((v) => v.version !== currentEntry.version);
    return [currentEntry, ...past];
  }, [versions, currentTemplate]);

  useEffect(() => {
    const load = async () => {
      if (!open || !templateId || !companyId || !companyType) return;
      try {
        setLoading(true);
        const rows = await emailTemplateApi.listEmailTemplateVersions(
          companyId,
          companyType,
          templateId,
        );
        setVersions(rows);
      } catch {
        notify.error("Failed to load versions");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [open, templateId, companyId, companyType]);

  const handleRestore = async (v: EmailTemplateVersion) => {
    if (!templateId || !companyId || !companyType) return;
    try {
      setRestoringId(v.id);
      await emailTemplateApi.restoreEmailTemplateVersion(
        companyId,
        companyType,
        templateId,
        v.id,
      );
      notify.success(`Restored version ${v.version}`);
      onRestored?.();
      onClose();
    } catch (e: any) {
      notify.error(typeof e === "string" ? e : "Failed to restore version");
    } finally {
      setRestoringId(null);
    }
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={
          <span>
            <HistoryOutlined /> Version History
          </span>
        }
        width={520}
        zIndex={1150}
        destroyOnClose
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 32 }}>
            <Spin />
          </div>
        ) : displayedVersions.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No previous versions yet — they will appear here on each save."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {displayedVersions.map((v) => {
              const isCurrent = v.id === CURRENT_VERSION_SENTINEL_ID;
              return (
              <div
                key={v.id}
                style={{
                  border: "1px solid #eef0f3",
                  borderRadius: 8,
                  padding: 12,
                  background: "#fff",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <div>
                    <Tag color={isCurrent ? "green" : "blue"} style={{ fontWeight: 600 }}>
                      {isCurrent ? `v${v.version} · Current` : `v${v.version}`}
                    </Tag>
                    <Text style={{ fontSize: 12, color: "#475569" }}>
                      {new Date(v.created_at).toLocaleString()}
                    </Text>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Tooltip title="Preview">
                      <Button
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => setPreviewVersion(v)}
                      />
                    </Tooltip>
                    {!isCurrent ? (
                      <Tooltip title="Restore this version">
                        <Button
                          size="small"
                          type="primary"
                          icon={<ReloadOutlined />}
                          loading={restoringId === v.id}
                          onClick={() => handleRestore(v)}
                        >
                          Restore
                        </Button>
                      </Tooltip>
                    ) : null}
                  </div>
                </div>
                <div style={{ fontSize: 12.5, color: "#0f172a", fontWeight: 500 }}>
                  {v.template_name}
                </div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                  {v.subject}
                </div>
                {v.change_note ? (
                  <div
                    style={{
                      fontSize: 11,
                      marginTop: 6,
                      padding: "4px 8px",
                      background: "#f8fafc",
                      borderRadius: 4,
                      color: "#475569",
                    }}
                  >
                    {v.change_note}
                  </div>
                ) : null}
              </div>
              );
            })}
          </div>
        )}
      </Drawer>

      <Drawer
        open={Boolean(previewVersion)}
        onClose={() => setPreviewVersion(null)}
        title={
          previewVersion ? (
            <span>
              Preview <Tag color="blue">v{previewVersion.version}</Tag>
            </span>
          ) : (
            "Preview"
          )
        }
        width="90vw"
        zIndex={1160}
        destroyOnClose
        styles={{ body: { padding: 16, display: "flex", flexDirection: "column" } }}
      >
        {previewVersion ? (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, flexShrink: 0 }}>
              <strong>Subject:</strong> {previewVersion.subject}
            </div>
            <iframe
              srcDoc={previewVersion.body_html}
              sandbox="allow-same-origin"
              title="version preview"
              style={{
                width: "100%",
                flex: 1,
                minHeight: "70vh",
                border: "1px solid #f0f0f0",
                borderRadius: 6,
                background: "#fff",
              }}
            />
          </>
        ) : null}
      </Drawer>
    </>
  );
};

export default TemplateVersionsDrawer;
